<template>
  <!-- One dropdown of checkboxes, "All origins" first; none ticked is any origin. -->
  <b-dropdown
    class="origin-filter"
    toggle-class="origin-filter__toggle"
    menu-class="origin-filter__menu"
    variant="link"
    no-caret
    block
    boundary="window"
    v-on="$listeners"
  >
    <template #button-content>
      <span
        class="origin-filter__text"
        :class="{ 'origin-filter__text--empty': !tags.length }"
        >{{ summary }}</span
      >
      <ChevronDown :size="16" class="origin-filter__caret" />
    </template>
    <b-dropdown-form form-class="origin-filter__form">
      <b-form-checkbox
        :checked="allPicked"
        :indeterminate="tags.length > 0 && !allPicked"
        class="origin-filter__all"
        data-cy="declined-origins-all"
        @change="$event ? pickAll() : clear()"
      >
        All origins
      </b-form-checkbox>
      <b-dropdown-divider />
      <b-form-checkbox
        v-for="option in originOptions"
        :key="option.origin"
        :checked="selectedOrigins.includes(option.origin)"
        :data-cy="`declined-origin-${option.origin}`"
        @change="toggle(option, $event)"
      >
        {{ option.text }}
      </b-form-checkbox>
    </b-dropdown-form>
  </b-dropdown>
</template>

<script>
import { Component, Vue } from 'vue-property-decorator'
import { ChevronDown } from 'lucide-vue'
import { PAYMENT_ORIGIN_LABELS } from '@/views/MessageCenter/condition.js'

/** The declined-payments origins, ticked in a dropdown; none ticked means any origin. */
@Component({ components: { ChevronDown } })
export default class DeclinedOriginFilter extends Vue {
  tags = []

  get originOptions() {
    return Object.entries(PAYMENT_ORIGIN_LABELS).map(([origin, text]) => ({
      text,
      origin
    }))
  }

  get selectedOrigins() {
    return this.tags.map(t => t.origin).filter(Boolean)
  }

  get allPicked() {
    return this.tags.length === this.originOptions.length
  }

  /** What the closed dropdown says: a count, as the box is too narrow for even one long origin name. */
  get summary() {
    if (!this.tags.length) return 'Any origin'
    if (this.allPicked) return 'All origins'

    return `${this.tags.length} ${this.tags.length === 1 ? 'origin' : 'origins'}`
  }

  toggle(option, checked) {
    const picked = new Set(this.selectedOrigins)
    if (checked) picked.add(option.origin)
    else picked.delete(option.origin)
    this.onTagsChanged(this.originOptions.filter(o => picked.has(o.origin)))
  }

  pickAll() {
    this.onTagsChanged(this.originOptions)
  }

  clear() {
    this.tags = []
    this.$emit('origins-changed', [])
  }

  onTagsChanged(newTags) {
    this.tags = newTags
    this.$emit('origins-changed', this.selectedOrigins)
  }

  setOrigins(values) {
    this.tags = values?.length
      ? this.originOptions.filter(option => values.includes(option.origin))
      : []
  }
}
</script>

<style lang="scss" scoped>
.origin-filter {
  flex: 1;
  min-width: 0;
}

// Drawn as the form's text inputs, so it sits level with the Days box beside it.
.origin-filter ::v-deep .origin-filter__toggle {
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: 38px;
  padding: 0.375rem 0.75rem;
  color: #212529;

  text-align: left;
  text-decoration: none;

  background: #fff;
  border: 1px solid #ced4da;
  border-radius: 4px;
}

.origin-filter__text {
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;

  &--empty {
    color: #6c757d;
  }
}

.origin-filter__caret {
  flex-shrink: 0;
  margin-left: 0.5rem;
  color: #6c757d;
}

.origin-filter ::v-deep .origin-filter__menu {
  max-height: 280px;
  overflow-y: auto;
}

.origin-filter ::v-deep .origin-filter__form {
  padding: 0.5rem 0.75rem;
  color: #212529;
  white-space: nowrap;

  .custom-control {
    padding-top: 2px;
    padding-bottom: 2px;
  }

  .custom-control-input:checked ~ .custom-control-label::before,
  .custom-control-input:indeterminate ~ .custom-control-label::before {
    background-color: $button-blue;
    border-color: $button-blue;
  }
}

.origin-filter__all {
  font-weight: 600;
}
</style>
